import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ArrowLeft, Loader2, Package, Save } from 'lucide-react';
import { toast } from 'sonner';
import { packingApiService, PackingDetail, PackingHeader, PackingStatus } from '../services/packingApi';
import { formatDate } from '../utils/dateUtils';

type QtyById = Record<number, string>;

const PackingEntryBoxesEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { batchId } = useParams<{ batchId: string }>();

  const [isLoading, setIsLoading] = useState(true);
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());

  const [batchHeaders, setBatchHeaders] = useState<PackingHeader[]>([]);
  const [packingDetails, setPackingDetails] = useState<PackingDetail[]>([]);
  const [qtyById, setQtyById] = useState<QtyById>({});

  useEffect(() => {
    if (!batchId) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const [headersRes, detailsRes] = await Promise.all([
          packingApiService.getPackingHeadersByBatchId(batchId),
          packingApiService.getPackingDetailsByBatchId(batchId),
        ]);

        const headers = headersRes.success && Array.isArray(headersRes.data) ? headersRes.data : [];
        const details = detailsRes.success && Array.isArray(detailsRes.data) ? detailsRes.data : [];

        setBatchHeaders(headers);
        setPackingDetails(details);
        setQtyById(
          details.reduce<QtyById>((acc, d) => {
            acc[d.id] = String(d.quantity ?? 0);
            return acc;
          }, {})
        );
      } catch {
        toast.error('Failed to load batch details');
        setBatchHeaders([]);
        setPackingDetails([]);
        setQtyById({});
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [batchId]);

  const headerSummary = useMemo(() => {
    if (batchHeaders.length === 0) return null;
    const first = batchHeaders[0];
    const quantity = batchHeaders.reduce((sum, h) => sum + (Number(h.quantity) || 0), 0);
    const rejectQuantity = batchHeaders.reduce((sum, h) => sum + (Number(h.rejectQuantity) || 0), 0);
    const status = first.status || PackingStatus.PACKED;
    const packedOn = first.packedOn;
    const packedBy = first.packedBy;
    return { quantity, rejectQuantity, status, packedOn, packedBy };
  }, [batchHeaders]);

  const changedDetailIds = useMemo(() => {
    const changed = new Set<number>();
    packingDetails.forEach((d) => {
      const current = Number(qtyById[d.id]);
      const original = Number(d.quantity ?? 0);
      if (!Number.isFinite(current)) return;
      if (current !== original) changed.add(d.id);
    });
    return changed;
  }, [packingDetails, qtyById]);

  // Global save removed: save is per-row only

  const handleSaveRow = async (detail: PackingDetail) => {
    if (!batchId) return;
    const detailId = detail.id;
    const nextQty = Number(qtyById[detailId]);
    if (!Number.isFinite(nextQty)) {
      toast.error('Please enter a valid quantity');
      return;
    }

    setSavingIds((prev) => new Set(prev).add(detailId));
    try {
      await packingApiService.updatePackingDetail(String(detailId), {
        id: detailId,
        headerBatchId: detail.headerBatchId,
        boxNumber: detail.boxNumber,
        status: detail.status,
        quantity: nextQty,
        notes: detail.notes,
      });

      // Update local cache to mark row as saved
      setPackingDetails((prev) =>
        prev.map((d) => (d.id === detailId ? { ...d, quantity: nextQty } : d))
      );
      toast.success(`Updated ${detail.boxNumber}`);
    } catch {
      toast.error(`Failed to update ${detail.boxNumber}`);
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(detailId);
        return next;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-6 py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-lg">Loading batch details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-6 py-6 space-y-6">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate(batchId ? `/packing-entries/batch/${batchId}` : '/packing-entries')}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Edit Box Quantities</h1>
            <p className="text-gray-600 mt-1">Batch: {batchId}</p>
          </div>
        </div>

        <div />
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <Card className="border border-gray-200 rounded-xl shadow-sm bg-white">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-100 rounded-t-xl">
            <CardTitle className="flex items-center text-primary-800 text-lg font-semibold">
              <Package className="h-5 w-5 mr-3 text-primary-600" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">Status</Label>
                <Badge className="border-0 font-semibold">{headerSummary?.status || 'N/A'}</Badge>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">Quantity</Label>
                <p className="text-xl font-bold text-green-600">{headerSummary?.quantity ?? 0}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">Reject Quantity</Label>
                <p className="text-xl font-bold text-gray-900">{headerSummary?.rejectQuantity ?? 0}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">Total Boxes</Label>
                <p className="text-xl font-bold text-blue-600">{packingDetails.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 rounded-xl shadow-sm bg-white lg:col-span-2">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-100 rounded-t-xl">
            <CardTitle className="flex items-center justify-between text-primary-800 text-lg font-semibold">
              <div className="flex items-center">
                <Package className="h-5 w-5 mr-3 text-primary-600" />
                Boxes
              </div>
              <span className="text-sm font-medium bg-primary-100 px-3 py-1 rounded-full text-primary-700">
                Changed: {changedDetailIds.size}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {packingDetails.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-primary-50 border-b-2 border-primary-200">
                      <th className="text-left py-3 px-4 font-bold text-primary-800 text-sm uppercase tracking-wide">Box Number</th>
                      <th className="text-left py-3 px-4 font-bold text-primary-800 text-sm uppercase tracking-wide">Status</th>
                      <th className="text-left py-3 px-4 font-bold text-primary-800 text-sm uppercase tracking-wide">Quantity</th>
                      <th className="text-left py-3 px-4 font-bold text-primary-800 text-sm uppercase tracking-wide">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packingDetails.map((d, index) => {
                      const changed = changedDetailIds.has(d.id);
                      const isRowSaving = savingIds.has(d.id);
                      return (
                        <tr
                          key={d.id}
                          className={`border-b border-primary-100 hover:bg-primary-50 transition-colors duration-200 ${
                            index % 2 === 0 ? 'bg-white' : 'bg-primary-25'
                          }`}
                        >
                          <td className="py-3 px-4 font-semibold text-gray-900 text-base">{d.boxNumber}</td>
                          <td className="py-3 px-4">
                            <Badge
                              variant={d.status === 'FULL' ? 'default' : 'outline'}
                              className={`${
                                d.status === 'FULL'
                                  ? 'bg-green-600 hover:bg-green-700 text-white border-green-600'
                                  : 'bg-amber-100 hover:bg-amber-200 text-amber-800 border-amber-300'
                              } font-semibold px-2 py-1 text-xs`}
                            >
                              {d.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Input
                              type="number"
                              inputMode="decimal"
                              className={`w-32 ${changed ? 'border-blue-400 ring-1 ring-blue-200' : ''}`}
                              value={qtyById[d.id] ?? ''}
                              onChange={(e) => setQtyById((prev) => ({ ...prev, [d.id]: e.target.value }))} 
                              min={0}
                            />
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSaveRow(d)}
                              disabled={!changed || isRowSaving}
                              className="border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                              title={!changed ? 'No changes' : 'Save this box'}
                            >
                              {isRowSaving ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Save className="h-4 w-4" />
                              )}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-6 py-8 text-center">
                <Package className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-500 text-lg">No boxes found for this batch</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PackingEntryBoxesEditPage;

